/*
MIT LICENSE
Copyright (c) 2020-present Alibaba Cloud Design
Copied from 'antd/components/dialog/confirm.tsx'.
Copyright (c) 2015-present Ant UED, https://xtech.antfin.com/

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE. */
import React from 'react';
import ReactDOM from 'react-dom';
import { getConfirmLocale } from './locale';
import ConfirmDialog from './ConfirmDialog';
import { globalConfig } from '../config-provider';
import warning from 'antd/es/_util/warning';
import destroyFns from './destroyFns';
import { Icon } from '../icon';
import { getMaskStyle } from '../_util/maskStyle';
let defaultRootPrefixCls = '';
function getRootPrefixCls() {
    return defaultRootPrefixCls;
}
export default function confirm(config) {
    const div = document.createElement('div');
    document.body.appendChild(div);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    let currentConfig = { ...config, close, visible: true };
    function destroy(...args) {
        const unmountResult = ReactDOM.unmountComponentAtNode(div);
        if (unmountResult && div.parentNode) {
            div.parentNode.removeChild(div);
        }
        const triggerCancel = args.some((param) => param && param.triggerCancel);
        if (config.onCancel && triggerCancel) {
            config.onCancel(...args);
        }
        for (let i = 0; i < destroyFns.length; i++) {
            const fn = destroyFns[i];
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            if (fn === close) {
                destroyFns.splice(i, 1);
                break;
            }
        }
    }
    function render({ okText, cancelText, prefixCls: customizePrefixCls, ...props }) {
        /**
         * https://github.com/ant-design/ant-design/issues/23623
         *
         * Sync render blocks React event. Let's make this async.
         */
        setTimeout(() => {
            const runtimeLocale = getConfirmLocale();
            //@pcj:globalConfig中没有getIconPrefixCls
            const { getPrefixCls, getAnimation, getIconPrefixCls } = globalConfig();
            // because Modal.config  set rootPrefixCls, which is different from other components
            const rootPrefixCls = getPrefixCls(undefined, getRootPrefixCls());
            const prefixCls = customizePrefixCls || `${rootPrefixCls}-modal`;
            const iconPrefixCls = getIconPrefixCls();
            const animation = getAnimation();
            ReactDOM.render(React.createElement(ConfirmDialog, { maskStyle: getMaskStyle(animation), ...props, prefixCls: prefixCls, rootPrefixCls: rootPrefixCls, iconPrefixCls: iconPrefixCls, okText: okText || (props.okCancel ? runtimeLocale.okText : runtimeLocale.justOkText), cancelText: cancelText || runtimeLocale.cancelText }), div);
        });
    }
    function close(...args) {
        currentConfig = {
            ...currentConfig,
            visible: false,
            afterClose: () => {
                if (typeof config.afterClose === 'function') {
                    config.afterClose();
                }
                // @ts-ignore
                destroy.apply(this, args);
            }
        };
        render(currentConfig);
    }
    function update(configUpdate) {
        if (typeof configUpdate === 'function') {
            currentConfig = configUpdate(currentConfig);
        }
        else {
            currentConfig = {
                ...currentConfig,
                ...configUpdate
            };
        }
        render(currentConfig);
    }
    render(currentConfig);
    destroyFns.push(close);
    return {
        destroy: close,
        update
    };
}
const commonProps = {
    width: 340,
    okText: '确定',
    cancelText: '取消',
    closable: true,
    closeIcon: React.createElement(Icon, { type: 'close', size: 'large' })
};
export function withWarn(props) {
    const { title = '注意', okButtonProps, cancelButtonProps, ...otherProps } = props;
    const okProps = { size: 'small', ...okButtonProps };
    const cancelProps = { size: 'small', type: 'default', ...cancelButtonProps };
    return {
        icon: React.createElement(Icon, { type: 'warning--filled' }),
        title: title,
        ...commonProps,
        ...otherProps,
        okButtonProps: okProps,
        cancelButtonProps: cancelProps,
        type: 'warning'
    };
}
export function withInfo(props) {
    const { okButtonProps, cancelButtonProps, ...otherProps } = props;
    const okProps = { size: 'small', ...okButtonProps };
    const cancelProps = { size: 'small', type: 'default', ...cancelButtonProps };
    return {
        icon: React.createElement(Icon, { type: 'information--filled' }),
        ...commonProps,
        ...otherProps,
        okButtonProps: okProps,
        cancelButtonProps: cancelProps,
        type: 'info'
    };
}
export function withSuccess(props) {
    const { title = '成功', okButtonProps, cancelButtonProps, ...otherProps } = props;
    const okProps = { size: 'small', ...okButtonProps };
    const cancelProps = { size: 'small', type: 'default', ...cancelButtonProps };
    return {
        icon: React.createElement(Icon, { type: 'checkmark--filled' }),
        title: title,
        ...commonProps,
        ...otherProps,
        okButtonProps: okProps,
        cancelButtonProps: cancelProps,
        type: 'success'
    };
}
export function withError(props) {
    const { title = '错误', okButtonProps, cancelButtonProps, ...otherProps } = props;
    const okProps = { size: 'small', ...okButtonProps };
    const cancelProps = { size: 'small', type: 'default', ...cancelButtonProps };
    return {
        icon: React.createElement(Icon, { type: 'warning--filled' }),
        title: title,
        ...commonProps,
        ...otherProps,
        okButtonProps: okProps,
        cancelButtonProps: cancelProps,
        type: 'error'
    };
}
export function withConfirm(props) {
    const { okButtonProps, cancelButtonProps, width = 340, ...otherProps } = props;
    const okProps = { size: 'small', ...okButtonProps };
    const cancelProps = { size: 'small', type: 'default', ...cancelButtonProps };
    return {
        icon: '',
        okCancel: true,
        ...commonProps,
        ...otherProps,
        okButtonProps: okProps,
        cancelButtonProps: cancelProps,
        type: 'confirm'
    };
}
export function modalGlobalConfig({ rootPrefixCls }) {
    if (process.env.NODE_ENV !== 'production')
        warning(false, 'Modal', 'Modal.config is deprecated. Please use ConfigProvider.config instead.');
    defaultRootPrefixCls = rootPrefixCls;
}
